import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../lib/i18n";

const LINKS = [
  ["home", "nav.home"],
  ["live", "nav.live"],
  ["process", "nav.process"],
  ["farmers", "nav.farmers"],
  ["faq", "nav.faq"],
  ["contact", "nav.contact"],
  ["reports", "nav.reports"],
];

const LangToggle = ({ testId }) => {
  const { lang, setLang, t } = useLang();
  return (
    <button
      data-testid={testId}
      onClick={() => setLang(lang === "hi" ? "en" : "hi")}
      aria-label={t("lang.label")}
      title={t("lang.label")}
      className="h-10 min-w-[44px] rounded-full border hairline px-3 text-sm font-semibold text-[var(--cream)] hover:bg-white/5 transition-colors"
    >
      {t("lang.switch")}
    </button>
  );
};

export const Nav = ({ onOrder }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (id) => {
    setOpen(false);
    if (id === "home") { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else if (id === "contact" || id === "reports") navigate(`/${id}`);
    else if (pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };
  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 border-b hairline bg-[#071a15]/70 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-5 lg:gap-8">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="text-sm text-[var(--sage)] hover:text-[var(--cream)] transition-colors">
              {t(label)}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle testId="lang-toggle" />
          <button
            data-testid="nav-order-btn"
            onClick={() => onOrder()}
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-[var(--husk)] text-[var(--forest)] px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
          >
            {t("cta.order")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2 text-[var(--cream)]" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t hairline bg-[var(--forest)] px-5 py-6 flex flex-col gap-5">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => go(id)} className="text-left text-2xl font-display">
              {t(label)}
            </button>
          ))}
          <button data-testid="mobile-order-btn" onClick={() => { setOpen(false); onOrder(); }} className="rounded-full bg-[var(--husk)] text-[var(--forest)] py-3 font-semibold">
            {t("cta.order")}
          </button>
        </div>
      )}
    </motion.header>
  );
};
